import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { addGameToFavoritesRequest, deleteGameFromFavoritesRequest, selectCasinoFavoriteById, selectUserIsAuthenticated, useQueryPopupOpen } from '@mwl/core-lib';
import { BreadcrumbsItem } from '@mwl/ui';
import FullScreenIcon from '@public/assets/casino/icons/fullScreen.svg';
import CloseIcon from '@public/assets/common/icons/close.svg';
import { BonusProgress } from '@/components/BonusProgress/BonusProgress';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Button } from '@/components/Button/Button';
import { ButtonFavorites } from '@/components/ButtonFavorites/ButtonFavorites';
import { ButtonIcon } from '@/components/ButtonIcon/ButtonIcon';
import { routes } from '@/constants';
import { useMediaWidthDown, useTypedDispatch, useTypedSelector } from '@/hooks';
import type { RootState } from '@/store/features/rootReducer';
import { casinoGamePageObject } from '@/utils';
import { useFullscreen } from '../../CasinoGamePage.context';
import { getFavoriteButtonAnalytics } from './Header.analytics';
import type { HeaderProps } from './Header.types';
import styles from './Header.module.scss';
export const Header: FC<HeaderProps> = ({
  mode,
  game
}) => {
  const {
    t
  } = useTranslation('casino');
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const addToFavoriteAfterAuth = useRef(false);
  const {
    createLinkProps,
    onOpenPopup
  } = useQueryPopupOpen();
  const linkProps = useMemo(() => createLinkProps({
    queryName: 'register'
  }), [createLinkProps]);
  const memoSelectCasinoFavoriteById = useCallback((state: RootState) => Boolean(game?.id && selectCasinoFavoriteById(state, game.id)), [game?.id]);
  const isXsScreen = useMediaWidthDown('screenXs');
  const isFavorite = useTypedSelector(memoSelectCasinoFavoriteById);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const {
    isFullscreen,
    onRequestExitFullscreen,
    onRequestFullscreen
  } = useFullscreen();
  const isLiveCasinoPage = router.pathname.startsWith(routes.liveCasino.home);
  const rootLabel = isLiveCasinoPage ? t('live_casino:page_name', 'Live casino') : t('casino:page_name', 'Casino');
  const rootHref = isLiveCasinoPage ? routes.liveCasino.home : routes.casino.home;
  const handleClickFavorite = useCallback(() => {
    if (!game) {
      return;
    }
    if (!isAuth) {
      onOpenPopup({
        queryName: 'register'
      });
      addToFavoriteAfterAuth.current = true;
      return;
    }
    if (isFavorite) {
      dispatch(deleteGameFromFavoritesRequest(game.id));
    } else {
      dispatch(addGameToFavoritesRequest(game.id));
    }
  }, [dispatch, game, isAuth, isFavorite, onOpenPopup]);
  const handleCloseGame = useCallback(() => router.push(isLiveCasinoPage ? routes.liveCasino.home : routes.casino.home), [isLiveCasinoPage, router]);
  useEffect(() => {
    if (isAuth && addToFavoriteAfterAuth.current && game) {
      addToFavoriteAfterAuth.current = false;
      dispatch(addGameToFavoritesRequest(game.id));
    }
  }, [dispatch, game, isAuth]);
  return <div className={styles.root} data-sentry-component="Header" data-sentry-source-file="Header.tsx">
      {game && <Breadcrumbs className={styles.breadcrumbs}>
          <BreadcrumbsItem href={rootHref} className={styles.breadcrumbLink} fontSize="sm" linkComponent={Link}>
            {rootLabel}
          </BreadcrumbsItem>
          <BreadcrumbsItem href={{
        pathname: routes.providers.provider,
        query: {
          providerName: game.provider.title
        }
      }} className={styles.breadcrumbLink} fontSize="sm" linkComponent={Link}>
            {game.provider.title}
          </BreadcrumbsItem>
          <BreadcrumbsItem href="" className={styles.breadcrumbLink} fontSize="sm" linkComponent={Link} isLastChild>
            {game.title}
          </BreadcrumbsItem>
        </Breadcrumbs>}
      <div className={styles.bonus}>
        {isAuth && !isXsScreen && mode === 'BONUS' && <BonusProgress />}
        {!isAuth && <div className={styles.modeButtons}>
            <Button className={cn(styles.modeButton, styles.playToReal)} as="link" {...linkProps} rounded color="grey" cut={false} variant="filled" fontWeight="bold" fontSize="lg">
              {t('game.play_to_real', 'Play to real')}
            </Button>
            <Button className={styles.modeButton} as="button" rounded color="red" cut={false} variant="filled" fontWeight="bold" fontSize="lg">
              {t('game.demo', 'Demo')}
            </Button>
          </div>}
      </div>
      <div className={styles.buttons}>
        <ButtonFavorites {...casinoGamePageObject.header.favorite.nodeProps} className={styles.actionButton} value={isFavorite} onClick={handleClickFavorite} color="red" variant="outlined" analytics={getFavoriteButtonAnalytics(isFavorite)} data-sentry-element="ButtonFavorites" data-sentry-source-file="Header.tsx" />
        <ButtonIcon {...casinoGamePageObject.header.fullscreen.nodeProps} className={styles.actionButton} as="button" onClick={isFullscreen ? onRequestExitFullscreen : onRequestFullscreen} aria-label="toggle fullscreen" data-sentry-element="ButtonIcon" data-sentry-source-file="Header.tsx">
          <FullScreenIcon className={styles.icon} data-sentry-element="FullScreenIcon" data-sentry-source-file="Header.tsx" />
        </ButtonIcon>
        <ButtonIcon {...casinoGamePageObject.header.close.nodeProps} className={styles.actionButton} as="button" onClick={handleCloseGame} aria-label="close game" data-sentry-element="ButtonIcon" data-sentry-source-file="Header.tsx">
          <CloseIcon className={styles.icon} data-sentry-element="CloseIcon" data-sentry-source-file="Header.tsx" />
        </ButtonIcon>
      </div>
    </div>;
};